import { useState, useEffect, Fragment } from 'react';
import { MdChevronRight } from 'react-icons/md';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, ReferenceLine,
} from 'recharts';
import { useAdvisor } from '../context/AdvisorContext';
import './HomeDashboard.css';

const SECTOR_COLORS = {
  'Energy':                 '#f97316',
  'Materials':              '#84cc16',
  'Industrials':            '#0ea5e9',
  'Consumer Discretionary': '#a855f7',
  'Consumer Staples':       '#14b8a6',
  'Health Care':            '#ec4899',
  'Financials':             '#eab308',
  'Information Technology': '#6366f1',
  'Communication Services': '#ef4444',
  'Utilities':              '#8b5cf6',
  'Real Estate':            '#64748b',
  'Other':                  '#94a3b8',
};
const sectorColor = (s) => SECTOR_COLORS[s] || SECTOR_COLORS['Other'];

const tooltipStyle = { backgroundColor: '#1c2230', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e6e8eb', fontSize: 12 };
const fmtDollar = v => `$${Number(v).toLocaleString()}`;

function TimeframeSwitch({ value, onChange }) {
  const buttons = [
    ['intraday', 'Intraday'],
    ['drivers',  'Drivers'],
    ['past-8',   'Past 8 Months'],
  ];
  return (
    <div className="timeframe-switch">
      {buttons.map(([key, label]) => (
        <button
          key={key}
          className={`timeframe-switch__btn ${value === key ? 'timeframe-switch__btn--active' : ''}`}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Past8Body({ data }) {
  const stratBase = data[0].Strategy;
  const benchBase = data[0]['S&P 500'];
  const rebased = data.map(d => ({
    date: d.date,
    Strategy:  (d.Strategy   / stratBase) * 10000,
    'S&P 500': (d['S&P 500'] / benchBase) * 10000,
  }));

  const last = rebased[rebased.length - 1];
  const stratDelta = ((last.Strategy   - 10000) / 100).toFixed(1);
  const benchDelta = ((last['S&P 500'] - 10000) / 100).toFixed(1);

  const all = rebased.flatMap(d => [d.Strategy, d['S&P 500']]);
  const lo  = Math.min(...all) * 0.95;
  const hi  = Math.max(...all) * 1.05;

  const xTickFmt = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  };

  return (
    <>
      <div className="subtitle-row">
        <p className="results-subtitle">
          Strategy → <strong style={{ color: stratDelta >= 0 ? '#00ff88' : '#ff5b5b' }}>{fmtDollar(Math.round(last.Strategy))} ({stratDelta >= 0 ? '+' : ''}{stratDelta}%)</strong>
          &nbsp;·&nbsp;
          S&P 500 → <strong>{fmtDollar(Math.round(last['S&P 500']))} ({benchDelta >= 0 ? '+' : ''}{benchDelta}%)</strong>
        </p>
        <p className="timeframe-note">Market data are delayed by up to 30 minutes.</p>
      </div>
      <div className="chart-wrap">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={rebased} margin={{ top: 5, right: 16, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="homeStratGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#00ff88" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#00ff88" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="homeBenchGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#5a6068" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#5a6068" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" tickFormatter={xTickFmt} tick={{ fontSize: 11, fill: '#5a6068' }} tickLine={false} />
            <YAxis domain={[lo, hi]} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} tick={{ fontSize: 11, fill: '#5a6068' }} tickLine={false} axisLine={false} width={52} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtDollar(Math.round(v)), name]} labelFormatter={l => `Date: ${l}`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="Strategy" stroke="#00ff88" strokeWidth={2}   fill="url(#homeStratGrad)" dot={false} />
            <Area type="monotone" dataKey="S&P 500" stroke="#5a6068" strokeWidth={1.5} fill="url(#homeBenchGrad)" dot={false} strokeDasharray="4 2" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

// Intraday: fixed X-axis 09:30 – 16:30 EST. Blank area past the last data point.
const INTRADAY_OPEN_MIN  = 9 * 60 + 30;
const INTRADAY_CLOSE_MIN = 16 * 60 + 30;
const timeToMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const minToTime = (n) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;

function nowEasternParts() {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false, weekday: 'short', hour: '2-digit', minute: '2-digit',
  });
  const parts = fmt.formatToParts(new Date());
  let weekday = '', h = 0, m = 0;
  for (const p of parts) {
    if (p.type === 'weekday') weekday = p.value;
    if (p.type === 'hour')    h = parseInt(p.value, 10);
    if (p.type === 'minute')  m = parseInt(p.value, 10);
  }
  if (h === 24) h = 0;
  return { weekday, h, m, mins: h * 60 + m };
}

function nowEasternMin() { return nowEasternParts().mins; }

// Markets open today only on weekdays after 09:30 ET. Weekends and pre-open
// mornings return false. (Holiday calendar not considered.)
function isMarketOpenOrPast() {
  const { weekday, mins } = nowEasternParts();
  if (weekday === 'Sat' || weekday === 'Sun') return false;
  return mins >= INTRADAY_OPEN_MIN;
}

function IntradayBody({ points }) {
  const nowET = nowEasternMin();
  // null prices → null chart values (recharts renders this as a gap, not $10k).
  const chartData = points.map(p => ({
    t: timeToMin(p.time),
    timeLabel: p.time,
    Strategy:  p.stratPct == null ? null : 10000 * (1 + p.stratPct / 100),
    'S&P 500': p.spPct    == null ? null : 10000 * (1 + p.spPct    / 100),
  })).filter(d =>
    !isNaN(d.t) &&
    d.t >= INTRADAY_OPEN_MIN &&
    d.t <= INTRADAY_CLOSE_MIN &&
    d.t <= nowET                       // never render points past the current ET wall clock
  );

  // Use the most recent row that has both values for the header summary.
  const lastValid = [...chartData].reverse().find(d => d.Strategy != null && d['S&P 500'] != null)
    || { Strategy: 10000, 'S&P 500': 10000, timeLabel: '—' };
  const stratDelta = ((lastValid.Strategy   - 10000) / 100).toFixed(2);
  const benchDelta = ((lastValid['S&P 500'] - 10000) / 100).toFixed(2);

  const all = chartData.flatMap(d => [d.Strategy, d['S&P 500']]).filter(v => v != null);
  const lo  = all.length ? Math.min(...all, 10000) * 0.995 : 9950;
  const hi  = all.length ? Math.max(...all, 10000) * 1.005 : 10050;

  const hourTicks = [];
  for (let m = INTRADAY_OPEN_MIN; m <= INTRADAY_CLOSE_MIN; m += 60) hourTicks.push(m);

  return (
    <>
      <div className="subtitle-row">
        <p className="results-subtitle">
          As of {lastValid.timeLabel} EST &nbsp;·&nbsp;
          Strategy → <strong style={{ color: stratDelta >= 0 ? '#00ff88' : '#ff5b5b' }}>{fmtDollar(Math.round(lastValid.Strategy))} ({stratDelta >= 0 ? '+' : ''}{stratDelta}%)</strong>
          &nbsp;·&nbsp;
          S&P 500 → <strong>{fmtDollar(Math.round(lastValid['S&P 500']))} ({benchDelta >= 0 ? '+' : ''}{benchDelta}%)</strong>
        </p>
        <p className="timeframe-note">Market data are delayed by up to 30 minutes.</p>
      </div>
      <div className="chart-wrap">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} margin={{ top: 5, right: 16, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="homeStratGradDay" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#00ff88" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#00ff88" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="homeBenchGradDay" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#5a6068" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#5a6068" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              type="number"
              dataKey="t"
              domain={[INTRADAY_OPEN_MIN, INTRADAY_CLOSE_MIN]}
              ticks={hourTicks}
              tickFormatter={minToTime}
              tick={{ fontSize: 11, fill: '#5a6068' }}
              tickLine={false}
            />
            <YAxis
              domain={[lo, hi]}
              tickFormatter={v => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              tick={{ fontSize: 11, fill: '#5a6068' }}
              tickLine={false}
              axisLine={false}
              width={62}
            />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtDollar(Math.round(v)), name]} labelFormatter={l => `${minToTime(l)} EST`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="Strategy" stroke="#00ff88" strokeWidth={2}   fill="url(#homeStratGradDay)" dot={false} />
            <Area type="monotone" dataKey="S&P 500" stroke="#5a6068" strokeWidth={1.5} fill="url(#homeBenchGradDay)" dot={false} strokeDasharray="4 2" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

function MarketClosedBody() {
  const hourTicks = [];
  for (let m = INTRADAY_OPEN_MIN; m <= INTRADAY_CLOSE_MIN; m += 60) hourTicks.push(m);
  // Pass two frame anchors so recharts renders the axes cleanly with no line.
  const frame = [
    { t: INTRADAY_OPEN_MIN  },
    { t: INTRADAY_CLOSE_MIN },
  ];

  return (
    <>
      <div className="subtitle-row">
        <p className="results-subtitle">Markets are closed.</p>
        <p className="timeframe-note">Market data are delayed by up to 30 minutes.</p>
      </div>
      <div className="chart-wrap chart-wrap--overlay">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={frame} margin={{ top: 5, right: 16, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              type="number"
              dataKey="t"
              domain={[INTRADAY_OPEN_MIN, INTRADAY_CLOSE_MIN]}
              ticks={hourTicks}
              tickFormatter={minToTime}
              tick={{ fontSize: 11, fill: '#5a6068' }}
              tickLine={false}
            />
            <YAxis
              domain={[9900, 10100]}
              tickFormatter={v => `$${v.toLocaleString()}`}
              tick={{ fontSize: 11, fill: '#5a6068' }}
              tickLine={false}
              axisLine={false}
              width={62}
            />
          </AreaChart>
        </ResponsiveContainer>
        <div className="chart-overlay">
          <div className="chart-overlay__title">Markets Closed</div>
          <div className="chart-overlay__body">Chart will populate after markets re-open.</div>
        </div>
      </div>
    </>
  );
}

function DriversBody({ drivers }) {
  const total = drivers.reduce((acc, d) => acc + d.contribution, 0);

  return (
    <>
      <div className="subtitle-row">
        <p className="results-subtitle">
          Stock-level contribution to portfolio P&amp;L since today's open
          &nbsp;·&nbsp;
          Total → <strong style={{ color: total >= 0 ? '#00ff88' : '#ff5b5b' }}>
            {total >= 0 ? '+' : ''}{total.toFixed(2)}%
          </strong>
        </p>
        <p className="timeframe-note">Market data are delayed by up to 30 minutes.</p>
      </div>
      <div className="chart-wrap">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={drivers} margin={{ top: 10, right: 16, left: 10, bottom: 36 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              dataKey="ticker"
              interval={0}
              angle={-45}
              textAnchor="end"
              height={56}
              tick={{ fontSize: 10, fill: '#64748b' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={v => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`}
              tick={{ fontSize: 11, fill: '#5a6068' }}
              tickLine={false}
              axisLine={false}
              width={62}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              cursor={{ fill: 'rgba(148,163,184,0.12)' }}
              formatter={(v) => [`${v >= 0 ? '+' : ''}${Number(v).toFixed(3)}%`, 'Contribution']}
              labelFormatter={(ticker, payload) => {
                const d = payload?.[0]?.payload;
                if (!d) return ticker;
                const stock = d.stockReturnPct;
                const stockStr = `${stock >= 0 ? '+' : ''}${stock.toFixed(2)}%`;
                return `${d.name} (${d.ticker}) · ${d.side} · stock ${stockStr}`;
              }}
            />
            <ReferenceLine y={0} stroke="#5a6068" />
            <Bar dataKey="contribution" radius={[2, 2, 0, 0]}>
              {drivers.map((d) => (
                <Cell key={d.ticker} fill={d.contribution >= 0 ? '#00ff88' : '#ff5b5b'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

function BriefingSwitch({ tabs, value, onChange }) {
  return (
    <div className="timeframe-switch">
      {tabs.map(([key, label]) => (
        <button
          key={key}
          className={`timeframe-switch__btn ${value === key ? 'timeframe-switch__btn--active' : ''}`}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function BriefingCard() {
  const {
    homeData, params,
    styleBrief, todayBrief, sinceWeSpokeBrief,
    fetchStyleBrief, fetchTodayBrief, fetchSinceWeSpokeBrief,
    isReturningUser,
  } = useAdvisor();

  // Returning users see a third tab (default), cold-start users get the
  // original two tabs.
  const tabs = isReturningUser
    ? [['since-spoke', 'Since We Spoke'], ['today', 'Today So Far'], ['portfolio', 'About This Portfolio']]
    : [['today', 'Today So Far'], ['portfolio', 'About This Portfolio']];
  const defaultTab = isReturningUser ? 'since-spoke' : 'today';
  const [view, setView] = useState(defaultTab);

  useEffect(() => {
    if (!homeData || !params) return;
    fetchStyleBrief().catch(() => {});
  }, [homeData, params]);

  const marketOk = isMarketOpenOrPast();
  useEffect(() => {
    if (!homeData || !params || !marketOk) return;
    fetchTodayBrief().catch(() => {});
  }, [homeData, params, marketOk]);

  useEffect(() => {
    if (!homeData || !params || !isReturningUser) return;
    fetchSinceWeSpokeBrief().catch(() => {});
  }, [homeData, params, isReturningUser]);

  if (!homeData || !params) return null;

  const todayContent = !marketOk
    ? <p className="briefing-card__body briefing-card__body--muted">Markets are closed — live driver commentary resumes at the next open.</p>
    : todayBrief.loading && !todayBrief.text
      ? <p className="briefing-card__body briefing-card__body--muted">Reading today's intraday tape…</p>
      : todayBrief.error
        ? <p className="briefing-card__body briefing-card__body--muted">⚠️ {todayBrief.error} &nbsp;<button className="link-btn" onClick={() => fetchTodayBrief().catch(() => {})}>Retry</button></p>
        : todayBrief.text
          ? (
            <p className="briefing-card__body">
              {todayBrief.text}
              {' '}
              <span className="briefing-card__nudge">
                Want more insights? Ask your Portfolio Manager using chat window on the right for more details, including specific questions about each of the portfolio holdings.
              </span>
            </p>
          )
          : <p className="briefing-card__body briefing-card__body--muted">—</p>;

  const styleContent = styleBrief.loading && !styleBrief.text
    ? <p className="briefing-card__body briefing-card__body--muted">Drafting portfolio description…</p>
    : styleBrief.error
      ? <p className="briefing-card__body briefing-card__body--muted">⚠️ {styleBrief.error} &nbsp;<button className="link-btn" onClick={() => fetchStyleBrief().catch(() => {})}>Retry</button></p>
      : styleBrief.text
        ? <p className="briefing-card__body">{styleBrief.text}</p>
        : <p className="briefing-card__body briefing-card__body--muted">—</p>;

  const sinceContent = !isReturningUser
    ? null
    : sinceWeSpokeBrief.loading && !sinceWeSpokeBrief.text
      ? <p className="briefing-card__body briefing-card__body--muted">Catching up on what's changed since our last conversation…</p>
      : sinceWeSpokeBrief.error
        ? <p className="briefing-card__body briefing-card__body--muted">⚠️ {sinceWeSpokeBrief.error} &nbsp;<button className="link-btn" onClick={() => fetchSinceWeSpokeBrief().catch(() => {})}>Retry</button></p>
        : sinceWeSpokeBrief.text
          ? <p className="briefing-card__body">{sinceWeSpokeBrief.text}</p>
          : <p className="briefing-card__body briefing-card__body--muted">—</p>;

  return (
    <section className="results-section briefing-card">
      <div className="briefing-card__header">
        <BriefingSwitch tabs={tabs} value={view} onChange={setView} />
      </div>
      <div className="briefing-card__scroll">
        {view === 'since-spoke' ? sinceContent : view === 'today' ? todayContent : styleContent}
      </div>
    </section>
  );
}

function PerformanceChart({ last8 }) {
  const { fetchIntraday, fetchDrivers, homeData } = useAdvisor();
  const [timeframe, setTimeframe] = useState('intraday');
  const [intraday, setIntraday]   = useState({ data: null, loading: false, error: '', closed: false });
  const [drivers, setDrivers]     = useState({ data: null, loading: false, error: '', closed: false });

  async function loadIntraday() {
    if (intraday.loading) return;
    setIntraday({ data: null, loading: true, error: '', closed: false });
    try {
      const pts = await fetchIntraday();
      setIntraday({ data: pts, loading: false, error: '', closed: false });
    } catch (e) {
      setIntraday({ data: null, loading: false, error: e?.message || 'Could not load intraday data.', closed: false });
    }
  }

  async function loadDrivers() {
    if (drivers.loading) return;
    setDrivers({ data: null, loading: true, error: '', closed: false });
    try {
      const rows = await fetchDrivers();
      setDrivers({ data: rows, loading: false, error: '', closed: false });
    } catch (e) {
      setDrivers({ data: null, loading: false, error: e?.message || 'Could not load drivers data.', closed: false });
    }
  }

  function handleTimeframe(tf) {
    setTimeframe(tf);
    if (tf === 'intraday') {
      if (!isMarketOpenOrPast()) {
        setIntraday({ data: null, loading: false, error: '', closed: true });
        return;
      }
      if (intraday.closed || (!intraday.data && !intraday.loading)) loadIntraday();
    } else if (tf === 'drivers') {
      if (!isMarketOpenOrPast()) {
        setDrivers({ data: null, loading: false, error: '', closed: true });
        return;
      }
      if (drivers.closed || (!drivers.data && !drivers.loading)) loadDrivers();
    }
  }

  // Intraday is the default timeframe, so on first render (or whenever
  // homeData first becomes available) fire the same load logic that
  // handleTimeframe would run on a manual click.
  useEffect(() => {
    if (timeframe !== 'intraday' || !homeData) return;
    if (!isMarketOpenOrPast()) {
      setIntraday(prev => prev.closed ? prev : { data: null, loading: false, error: '', closed: true });
      return;
    }
    if (!intraday.data && !intraday.loading && !intraday.closed && !intraday.error) {
      loadIntraday();
    }
  }, [homeData]);

  const title =
    timeframe === 'intraday' ? 'Intraday' :
    timeframe === 'drivers'  ? "Today's Drivers" :
                               'Past 8 Months';

  return (
    <section className="results-section">
      <div className="chart-header-row">
        <h2 className="results-title">{title}</h2>
        <TimeframeSwitch value={timeframe} onChange={handleTimeframe} />
      </div>

      {timeframe === 'past-8' && last8.length >= 2 && <Past8Body data={last8} />}

      {timeframe === 'intraday' && intraday.closed && <MarketClosedBody />}

      {timeframe === 'intraday' && !intraday.closed && intraday.loading && (
        <p className="results-subtitle">Fetching today's intraday prices…</p>
      )}
      {timeframe === 'intraday' && !intraday.closed && intraday.error && (
        <p className="results-subtitle">⚠️ {intraday.error}
          &nbsp;<button className="link-btn" onClick={loadIntraday}>Retry</button>
        </p>
      )}
      {timeframe === 'intraday' && !intraday.closed && intraday.data && intraday.data.length > 0 && (
        <IntradayBody points={intraday.data} />
      )}
      {timeframe === 'intraday' && !intraday.closed && intraday.data && intraday.data.length === 0 && !intraday.loading && (
        <p className="results-subtitle">No intraday data points returned.</p>
      )}

      {timeframe === 'drivers' && drivers.closed && <MarketClosedBody />}
      {timeframe === 'drivers' && !drivers.closed && drivers.loading && (
        <p className="results-subtitle">Computing today's drivers…</p>
      )}
      {timeframe === 'drivers' && !drivers.closed && drivers.error && (
        <p className="results-subtitle">⚠️ {drivers.error}
          &nbsp;<button className="link-btn" onClick={loadDrivers}>Retry</button>
        </p>
      )}
      {timeframe === 'drivers' && !drivers.closed && drivers.data && drivers.data.length > 0 && (
        <DriversBody drivers={drivers.data} />
      )}
      {timeframe === 'drivers' && !drivers.closed && drivers.data && drivers.data.length === 0 && !drivers.loading && (
        <p className="results-subtitle">No driver data available yet — waiting for today's first intraday prices.</p>
      )}
    </section>
  );
}

function PositionsPanel({ title, rows, sleevePct, barColor, sign = 1 }) {
  const { describeCompany, descCache } = useAdvisor();
  const [expandedTicker, setExpandedTicker] = useState(null);
  const [loadingTicker, setLoadingTicker]   = useState(null);

  const portfolioWeights = rows.map(r => r.weight * sleevePct);
  const maxPw = Math.max(...portfolioWeights, 0.01);

  async function toggle(row) {
    if (expandedTicker === row.ticker) { setExpandedTicker(null); return; }
    setExpandedTicker(row.ticker);
    if (!descCache[row.ticker]) {
      setLoadingTicker(row.ticker);
      try { await describeCompany(row.ticker, row.name); }
      finally { setLoadingTicker(prev => prev === row.ticker ? null : prev); }
    }
  }

  return (
    <section className="results-section home-panel">
      <h3 className="home-panel__title">{title}</h3>
      <table className="holdings-table">
        <thead>
          <tr><th>Security</th><th className="holdings-table__wt">%</th><th className="holdings-table__bar-col"></th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const pw = portfolioWeights[i];
            const isOpen = expandedTicker === r.ticker;
            return (
              <Fragment key={r.ticker}>
                <tr
                  className={`holdings-table__row ${isOpen ? 'holdings-table__row--open' : ''}`}
                  onClick={() => toggle(r)}
                >
                  <td className="holdings-table__name">
                    <MdChevronRight className="holdings-table__chev" size={16} />
                    <span className="holdings-table__company">{r.name}</span>
                    <span className="holdings-table__ticker"> ({r.ticker})</span>
                  </td>
                  <td className="holdings-table__wt">{(sign * pw).toFixed(2)}%</td>
                  <td className="holdings-table__bar-col">
                    <div className="holdings-bar">
                      <div className="holdings-bar__fill" style={{ width: `${(pw / maxPw) * 100}%`, background: barColor }} />
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="holdings-table__detail">
                    <td colSpan={3}>
                      {loadingTicker === r.ticker
                        ? 'Loading description…'
                        : (descCache[r.ticker] || 'Description unavailable.')}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function NetSectorPanel({ netSectors }) {
  const data = netSectors.map(s => ({ ...s, value: Math.abs(s.net) }));
  return (
    <section className="results-section home-panel">
      <h3 className="home-panel__title">Sector Mix (Net)</h3>
      <div className="home-donut-wrap">
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="sector"
              cx="50%" cy="50%"
              innerRadius={40}
              outerRadius={70}
              paddingAngle={2}
              stroke="#fff"
              strokeWidth={1.5}
            >
              {data.map(d => <Cell key={d.sector} fill={sectorColor(d.sector)} />)}
            </Pie>
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v, name, p) => {
                const net = p.payload?.net ?? 0;
                return [`${net >= 0 ? '+' : ''}${net.toFixed(2)}%`, name];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="sector-legend">
        {netSectors.map(s => (
          <li key={s.sector} className="sector-legend__item">
            <span className="sector-legend__dot" style={{ background: sectorColor(s.sector) }} />
            <span className="sector-legend__name">{s.sector}</span>
            <span className="sector-legend__val" style={{ color: s.net >= 0 ? '#e6e8eb' : '#ff5b5b' }}>
              {s.net >= 0 ? '+' : ''}{s.net.toFixed(2)}%
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const TABS = [
  { key: 'portfolio',       label: 'Portfolio' },
  { key: 'markets-context', label: 'Markets & Context' },
];

const MARKETS_VIEWS = [
  ['today',   'Markets Today'],
  ['drivers', 'Your Portfolio Drivers'],
];

function MarketsViewSwitch({ value, onChange }) {
  return (
    <div className="timeframe-switch">
      {MARKETS_VIEWS.map(([key, label]) => (
        <button
          key={key}
          className={`timeframe-switch__btn ${value === key ? 'timeframe-switch__btn--active' : ''}`}
          onClick={() => onChange(key)}
          type="button"
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// Maps theme.intensity (0–1) to a pill font size. Larger values dominate the
// cell visually — the "more impact = bigger text" signal from the word-cloud
// idea, but constrained inside each quadrant so the 2×2 structure still reads.
function pillFontSize(intensity) {
  const i = Math.max(0, Math.min(1, Number.isFinite(intensity) ? intensity : 0.5));
  return 11 + i * 11; // 11px → 22px
}

function DriversCell({ title, polarity, magnitude, themes, focusedIdx, allThemes, onFocus }) {
  return (
    <div className={`drivers-cell drivers-cell--${polarity} drivers-cell--${magnitude}`}>
      <div className="drivers-cell__header">{title}</div>
      {themes.length === 0 ? (
        <p className="drivers-cell__empty">No themes in this quadrant.</p>
      ) : (
        <div className="drivers-pills">
          {themes.map((t) => {
            const idx = allThemes.indexOf(t);
            const isActive = idx === focusedIdx;
            return (
              <button
                key={`${t.name}-${idx}`}
                className={`drivers-pill drivers-pill--${polarity} ${isActive ? 'drivers-pill--active' : ''}`}
                style={{ fontSize: `${pillFontSize(t.intensity)}px` }}
                onClick={() => onFocus(isActive ? null : idx)}
                type="button"
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PortfolioDrivers() {
  const {
    homeData, params,
    marketsHeadlines, marketsThemes,
    fetchThemes, setSelectedHeadlineId, setMarketsView,
  } = useAdvisor();
  const [focusedIdx, setFocusedIdx] = useState(null);

  useEffect(() => {
    if (!homeData || !params) return;
    if (marketsThemes.loading) return;
    if (marketsThemes.error) return;             // let the user retry manually
    if (marketsThemes.items.length) return;
    fetchThemes().catch(() => {});
  }, [homeData, params, marketsThemes.loading, marketsThemes.error, marketsThemes.items.length]);

  useEffect(() => { setFocusedIdx(null); }, [marketsThemes.items]);

  if (marketsThemes.loading && !marketsThemes.items.length) {
    return <section className="results-section markets-drivers"><p className="markets-context__hint">Distilling themes from today's news flow…</p></section>;
  }
  if (marketsThemes.error) {
    return (
      <section className="results-section markets-drivers">
        <p className="markets-context__hint">
          ⚠️ {marketsThemes.error}
          &nbsp;<button className="link-btn" onClick={() => fetchThemes().catch(() => {})}>Retry</button>
        </p>
      </section>
    );
  }
  if (!marketsThemes.items.length) {
    return <section className="results-section markets-drivers"><p className="markets-context__hint">Waiting for headlines…</p></section>;
  }

  const themes = marketsThemes.items;
  const bucket = (pol, mag) => themes.filter(t => t.polarity === pol && t.magnitude === mag);

  const focused = focusedIdx != null ? themes[focusedIdx] : null;
  const linkedHeadlines = focused
    ? (focused.linkedHeadlineIds || [])
        .map(id => marketsHeadlines.items.find(h => h.id === id))
        .filter(Boolean)
    : [];

  const jumpToHeadline = (id) => {
    setSelectedHeadlineId(id);
    setMarketsView('today');
  };

  return (
    <section className="results-section markets-drivers">
      <div className="drivers-axis drivers-axis--top">
        <span>POSITIVE FOR PORTFOLIO</span>
        <span>NEGATIVE FOR PORTFOLIO</span>
      </div>
      <div className="drivers-grid">
        <DriversCell title="Major positive" polarity="positive" magnitude="major" themes={bucket('positive', 'major')} allThemes={themes} focusedIdx={focusedIdx} onFocus={setFocusedIdx} />
        <DriversCell title="Major negative" polarity="negative" magnitude="major" themes={bucket('negative', 'major')} allThemes={themes} focusedIdx={focusedIdx} onFocus={setFocusedIdx} />
        <DriversCell title="Minor positive" polarity="positive" magnitude="minor" themes={bucket('positive', 'minor')} allThemes={themes} focusedIdx={focusedIdx} onFocus={setFocusedIdx} />
        <DriversCell title="Minor negative" polarity="negative" magnitude="minor" themes={bucket('negative', 'minor')} allThemes={themes} focusedIdx={focusedIdx} onFocus={setFocusedIdx} />
      </div>

      {focused && (
        <div className={`drivers-detail drivers-detail--${focused.polarity}`}>
          <div className="drivers-detail__name">{focused.name}</div>
          <p className="drivers-detail__rationale">{focused.rationale}</p>
          {linkedHeadlines.length > 0 && (
            <div className="drivers-detail__links">
              <div className="drivers-detail__links-label">Source headlines</div>
              <ul className="drivers-detail__links-list">
                {linkedHeadlines.map(h => (
                  <li key={h.id}>
                    <button className="link-btn" onClick={() => jumpToHeadline(h.id)} type="button">
                      {h.headline}
                    </button>
                    <span className="drivers-detail__links-source"> · {h.source}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function MarketsToday() {
  const {
    marketsHeadlines, marketsAnalysis, selectedHeadlineId,
    fetchHeadlines, analyzeHeadline, setSelectedHeadlineId,
  } = useAdvisor();

  useEffect(() => {
    if (!marketsHeadlines.items.length && !marketsHeadlines.loading && !marketsHeadlines.error) {
      fetchHeadlines().catch(() => {});
    }
  }, []);

  // Auto-select the first headline once loaded.
  useEffect(() => {
    if (!selectedHeadlineId && marketsHeadlines.items.length) {
      setSelectedHeadlineId(marketsHeadlines.items[0].id);
    }
  }, [marketsHeadlines.items, selectedHeadlineId, setSelectedHeadlineId]);

  // Kick off analysis whenever the selected headline changes and we don't
  // already have a cached / in-flight result for it.
  useEffect(() => {
    if (!selectedHeadlineId) return;
    const item = marketsHeadlines.items.find(it => it.id === selectedHeadlineId);
    if (!item) return;
    const cached = marketsAnalysis[selectedHeadlineId];
    if (cached?.summary && cached?.impact) return;
    if (cached?.loading) return;
    analyzeHeadline(item).catch(() => {});
  }, [selectedHeadlineId, marketsHeadlines.items]);

  const selected = marketsHeadlines.items.find(it => it.id === selectedHeadlineId);
  const analysis = selectedHeadlineId ? marketsAnalysis[selectedHeadlineId] : null;
  const pending  = selected && (!analysis || analysis.loading);

  return (
    <section className="results-section markets-context">
      <div className="markets-context__list">
        {marketsHeadlines.loading && (
          <p className="markets-context__hint">Fetching headlines…</p>
        )}
        {marketsHeadlines.error && (
          <p className="markets-context__hint">
            ⚠️ {marketsHeadlines.error}
            &nbsp;<button className="link-btn" onClick={() => fetchHeadlines().catch(() => {})}>Retry</button>
          </p>
        )}
        {!marketsHeadlines.loading && !marketsHeadlines.error && marketsHeadlines.items.length === 0 && (
          <p className="markets-context__hint">
            No headlines available right now.
            &nbsp;<button className="link-btn" onClick={() => fetchHeadlines().catch(() => {})}>Retry</button>
          </p>
        )}
        {marketsHeadlines.items.map(it => (
          <button
            key={it.id}
            className={`markets-context__item ${selectedHeadlineId === it.id ? 'markets-context__item--active' : ''}`}
            onClick={() => setSelectedHeadlineId(it.id)}
            type="button"
          >
            <span className="markets-context__item-headline">{it.headline}</span>
            <span className="markets-context__item-meta">{it.source}</span>
          </button>
        ))}
      </div>

      <div className="markets-context__detail">
        {!selected ? (
          <p className="markets-context__hint">Select a headline to see the analysis.</p>
        ) : (
          <>
            <a
              href={selected.url}
              target="_blank"
              rel="noreferrer"
              className="markets-context__source-link"
            >
              {selected.headline} ↗
            </a>
            <div className="markets-context__source-meta">
              {selected.source}
              {selected.datetime ? ` · ${new Date(selected.datetime * 1000).toLocaleString()}` : ''}
            </div>

            <h3 className="markets-context__section-title">Summary</h3>
            {pending ? (
              <p className="markets-context__body markets-context__body--muted">Analyzing…</p>
            ) : analysis?.error ? (
              <p className="markets-context__body markets-context__body--muted">
                ⚠️ {analysis.error}
                &nbsp;<button className="link-btn" onClick={() => analyzeHeadline(selected).catch(() => {})}>Retry</button>
              </p>
            ) : (
              <p className="markets-context__body">{analysis?.summary}</p>
            )}

            <h3 className="markets-context__section-title">Impact on Your Portfolio</h3>
            {pending ? (
              <p className="markets-context__body markets-context__body--muted">Analyzing…</p>
            ) : analysis?.error ? (
              <p className="markets-context__body markets-context__body--muted">—</p>
            ) : (
              <p className="markets-context__body">{analysis?.impact}</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function MarketsContext() {
  const { marketsView, setMarketsView } = useAdvisor();
  return (
    <>
      <div className="markets-toolbar">
        <MarketsViewSwitch value={marketsView} onChange={setMarketsView} />
      </div>
      {marketsView === 'drivers' ? <PortfolioDrivers /> : <MarketsToday />}
    </>
  );
}

export default function HomeDashboard() {
  const { deployed, backtestData, homeData } = useAdvisor();
  const [activeTab, setActiveTab] = useState('portfolio');

  if (!deployed) {
    return (
      <div className="home-placeholder">
        <p className="home-placeholder__title">No deployed strategy yet</p>
        <p className="home-placeholder__body">Head to the Strategy tab to build and deploy a portfolio. Once deployed, this dashboard will show your recent performance and current positions.</p>
      </div>
    );
  }

  const last8 = backtestData ? backtestData.slice(-8) : [];

  return (
    <div className="results-scroll">
      <div className="home-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`home-tab ${activeTab === t.key ? 'home-tab--active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'portfolio' && (
        <>
          <BriefingCard />
          {last8.length >= 2 && <PerformanceChart last8={last8} />}

          {!homeData ? (
            <section className="results-section"><p className="results-subtitle">Loading current portfolio snapshot…</p></section>
          ) : (
            <div className="home-panels">
              <PositionsPanel
                title="Stocks Expected to Rise (Long Positions)"
                rows={homeData.longs}
                sleevePct={homeData.longPct}
                barColor="#00ff88"
              />
              <PositionsPanel
                title="Drop Hedge (Short Positions)"
                rows={homeData.shorts}
                sleevePct={homeData.shortPct}
                barColor="#ff5b5b"
                sign={-1}
              />
              <NetSectorPanel netSectors={homeData.netSectors} />
            </div>
          )}
        </>
      )}

      {activeTab === 'markets-context' && <MarketsContext />}
    </div>
  );
}
